'use client';

import React from 'react';
import { Shield, RefreshCw, Zap, ArrowRight, Lightbulb, Check } from 'lucide-react';

interface Props {
  onContinue: () => void;
  onSwitchToA: () => void;
}

export default function Slide6BProduct({ onContinue, onSwitchToA }: Props) {
  return (
    <div className="relative min-h-screen flex flex-col items-center justify-center px-4 py-16 overflow-hidden">
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background: 'radial-gradient(ellipse 60% 50% at 50% 30%, rgba(0,168,255,0.07) 0%, transparent 70%)',
        }}
      />

      <div className="max-w-4xl w-full mx-auto fade-in">
        <div className="text-center mb-10">
          <p className="text-primary text-sm font-semibold tracking-widest uppercase mb-3">
            Product Pricing
          </p>
          <h2 className="text-4xl sm:text-5xl font-extrabold text-foreground leading-tight mb-5">
            Choose Your <span className="gradient-text">X39 Experience</span>
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-6">
          {/* Retail Card */}
          <div className="glass-card p-7">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl bg-blue-500/10 border border-blue-400/20 flex items-center justify-center">
                <Shield size={18} className="text-blue-400" />
              </div>
              <div>
                <p className="text-foreground font-bold text-lg">Retail</p>
                <p className="text-muted-foreground text-xs">One-time order, no commitment</p>
              </div>
            </div>
            <div className="mb-4">
              <span className="text-4xl font-extrabold text-foreground tabular-nums">$149.95</span>
              <span className="text-muted-foreground text-sm ml-2">/ sleeve</span>
              <p className="text-muted-foreground text-xs mt-1">+ tax &amp; shipping</p>
            </div>
            <ul className="space-y-2 mb-5">
              {[
                '30 patches (1 month supply)',
                '90-day 100% money back guarantee',
                'Order anytime, no subscription',
              ].map((item) => (
                <li key={`retail-${item}`} className="flex items-center gap-2 text-sm text-secondary-foreground">
                  <Check size={13} className="text-blue-400 flex-shrink-0" />
                  {item}
                </li>
              ))}
            </ul>
            <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-green-500/10 border border-green-500/20 text-green-400 text-xs font-semibold">
              <Shield size={11} />
              90-Day Money Back
            </div>
          </div>

          {/* Preferred Customer Cards */}
          <div className="space-y-4">
            <div className="glass-card p-6 border-primary/20" style={{ background: 'rgba(0,168,255,0.05)' }}>
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <RefreshCw size={16} className="text-primary" />
                  <span className="text-foreground font-bold">1 Sleeve / Month</span>
                </div>
                <span className="text-xs text-primary font-semibold px-2 py-0.5 rounded-full border border-primary/30 bg-primary/10">
                  Preferred Customer
                </span>
              </div>
              <div className="mb-2">
                <span className="text-3xl font-extrabold text-foreground tabular-nums">$99.95</span>
                <span className="text-muted-foreground text-sm ml-1">/month</span>
                <p className="text-muted-foreground text-xs mt-0.5">+ tax &amp; shipping</p>
              </div>
              <p className="text-xs text-muted-foreground">Requires active autoship to maintain wholesale pricing</p>
            </div>

            <div className="glass-card p-6 border-accent/20" style={{ background: 'rgba(0,229,255,0.05)' }}>
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Zap size={16} className="text-accent" />
                  <span className="text-foreground font-bold">2 Sleeves / Month</span>
                </div>
                <span className="text-xs text-accent font-semibold px-2 py-0.5 rounded-full border border-accent/30 bg-accent/10">
                  Preferred Customer
                </span>
              </div>
              <div className="mb-2">
                <span className="text-3xl font-extrabold text-foreground tabular-nums">$199.90</span>
                <span className="text-muted-foreground text-sm ml-1">/month</span>
                <p className="text-muted-foreground text-xs mt-0.5">+ tax &amp; shipping</p>
              </div>
              <p className="text-xs text-muted-foreground">Best value — covers you and a partner. Requires active autoship to maintain wholesale pricing.</p>
            </div>
          </div>
        </div>

        {/* Secondary Hook */}
        <div
          className="rounded-2xl p-6 mb-8 border"
          style={{
            background: 'linear-gradient(135deg, rgba(0,229,255,0.06) 0%, rgba(0,168,255,0.08) 100%)',
            borderColor: 'rgba(0,229,255,0.3)',
            boxShadow: '0 0 30px rgba(0,229,255,0.1)',
          }}
        >
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 rounded-xl bg-accent/10 border border-accent/30 flex items-center justify-center flex-shrink-0">
              <Lightbulb size={20} className="text-accent" />
            </div>
            <div className="flex-1">
              <h4 className="text-foreground font-bold text-lg mb-1">
                💡 Are you interested in potentially getting your patches{' '}
                <span className="gradient-text">paid for?</span>
              </h4>
              <p className="text-secondary-foreground text-sm leading-relaxed mb-4">
                Many of our customers offset or completely eliminate their monthly cost by simply
                sharing X39 with others. It doesn&apos;t require becoming a salesperson — just sharing
                something that&apos;s working for you.
              </p>
              <button
                onClick={onSwitchToA}
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl font-semibold text-sm text-accent border border-accent/40 hover:bg-accent/10 transition-all"
              >
                Tell Me More
                <ArrowRight size={15} />
              </button>
            </div>
          </div>
        </div>

        <div className="text-center">
          <button onClick={onContinue} className="btn-primary inline-flex items-center gap-2">
            Continue to Order
            <ArrowRight size={18} />
          </button>
        </div>
      </div>
    </div>
  );
}